/**
 * ADR-0185 Phase A — the graded change sheet, BELOW the lanes on the one
 * Board view (owner-ruled: no tabs anywhere in the loop). The minimal set of
 * Plex entries that makes the shop match the plan's intent, graded:
 *
 *   ② Directly impacted — needs Plex attention (machine moved, pulled
 *     into / pushed out of the near window, or promise implications), with a
 *     live self-check against the current book per row;
 *   ③ Downstream — no Plex entry needed (Plex replans these itself once ②
 *     is entered; collapsed to a count by default).
 *
 *   ① Your moves (Phase B) — the planner's own hand moves; always entered in
 *     Plex; blue stripe, first; Changing-to exists only after the replan that
 *     produced it; self-checked like ②.
 *
 * All composition is pure (sheetMath.ts); this file only renders. Timestamps
 * absolute, always.
 */
import { useState } from "react";
import { formatTimestamp } from "../../../../lib/formatTime.js";
import type { ChangeSheet as ChangeSheetData, ChangeSheetRow } from "../../boardTypes.js";
import { jobLabel } from "./laneMath.js";
import { changeSummary, machineCell, sheetCountLine, sheetStatus, shortDay } from "./sheetMath.js";

function ChangeCell({ row }: { row: ChangeSheetRow }) {
  return (
    <span className="boardSheetChange">
      {changeSummary(row).map((seg, i) =>
        seg.bold ? <strong key={i}>{seg.text}</strong> : <span key={i}>{seg.text}</span>
      )}
    </span>
  );
}

function StatusCell({ row, liveAvailable }: { row: ChangeSheetRow; liveAvailable: boolean }) {
  const status = sheetStatus(row, liveAvailable);
  const cls =
    status.tone === "done"
      ? "boardSheetStatusDone"
      : status.tone === "auto"
        ? "boardSheetStatusAuto"
        : "boardSheetStatusWait";
  return <span className={cls}>{status.text}</span>;
}

function RowCells({ row, liveAvailable }: { row: ChangeSheetRow; liveAvailable: boolean }) {
  return (
    <tr>
      <td className="mono">{jobLabel(row)}</td>
      <td>{machineCell(row)}</td>
      <td>
        <ChangeCell row={row} />
      </td>
      <td className="mono">
        {row.from_start ? formatTimestamp(row.from_start) : "—"}
        {row.machine_changed && row.machine_from ? <span className="muted"> ({row.machine_from})</span> : null}
      </td>
      <td className="mono">
        {row.grade === 3 ? (
          <span className="boardSheetStatusAuto">
            Plex sets it{row.to_start ? ` (≈ ${shortDay(row.to_start)})` : ""}
          </span>
        ) : (
          <>
            <strong>{row.to_start ? formatTimestamp(row.to_start) : "—"}</strong>
            {row.machine_changed ? <span className="muted"> ({row.machine_to})</span> : null}
          </>
        )}
      </td>
      <td>
        <StatusCell row={row} liveAvailable={liveAvailable} />
      </td>
    </tr>
  );
}

function TierTable({ rows, liveAvailable }: { rows: ChangeSheetRow[]; liveAvailable: boolean }) {
  return (
    <div className="tableWrap">
      <table className="table boardSheetTable">
        <thead>
          <tr>
            <th>Job (part)</th>
            <th>Machine</th>
            <th>Change</th>
            <th>
              Changing from <span className="muted boardSheetHint">(in Plex now)</span>
            </th>
            <th>
              Changing to <span className="muted boardSheetHint">(enter this)</span>
            </th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <RowCells key={`${r.job}-${r.machine_to}`} row={r} liveAvailable={liveAvailable} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ChangeSheet({ sheet, target = "Optimized" }: { sheet: ChangeSheetData | null; target?: "Optimized" | "Refined" }) {
  const [showDownstream, setShowDownstream] = useState(false);

  if (!sheet) {
    return (
      <div className="card">
        <strong>The changes — Plex schedule → {target}</strong>
        <div className="help" style={{ marginTop: 4 }}>
          {target === "Refined" ? "The change sheet arrives with the next replan." : "The change sheet arrives with the next solve."}
        </div>
      </div>
    );
  }

  const yours = sheet.rows.filter((r) => r.grade === 1);
  const direct = sheet.rows.filter((r) => r.grade === 2);
  const downstream = sheet.rows.filter((r) => r.grade === 3);
  const liveAvailable = sheet.live_check.available;
  const showingYours = yours.filter((r) => r.check?.state === "showing").length;

  return (
    <div className="card">
      <strong>The changes — Plex schedule → {target}</strong>
      <div className="muted boardSheetStamp">
        Compared against the Plex schedule as of {sheet.baseline_as_of ? formatTimestamp(sheet.baseline_as_of) : "—"}
        {" · "}
        {sheet.needing_action > 0 || !liveAvailable ? <strong>{sheetCountLine(sheet)}</strong> : "nothing needs Plex attention"}
        {" · changes made in Plex appear here within a few hours"}
      </div>

      {yours.length > 0 ? (
        <div className="boardSheetTier boardSheetTierYours">
          <div className="boardSheetTierHead">
            <span className="boardSheetGrade boardSheetGradeYours">①</span>{" "}
            <strong>Your moves</strong> — enter these in Plex{" "}
            <span className="muted boardSheetHint">
              · {yours.length} {yours.length === 1 ? "entry" : "entries"}
              {liveAvailable ? ` · ${showingYours} showing in Plex` : ""}
            </span>
          </div>
          <TierTable rows={yours} liveAvailable={liveAvailable} />
        </div>
      ) : null}

      <div className="boardSheetTier boardSheetTierDirect">
        <div className="boardSheetTierHead">
          <span className="boardSheetGrade boardSheetGradeDirect">②</span>{" "}
          <strong>Directly impacted — needs Plex attention</strong>{" "}
          <span className="muted boardSheetHint">(hit hard in the next {sheet.near_window_days} days)</span>{" "}
          <span className="muted boardSheetHint">· {direct.length} {direct.length === 1 ? "entry" : "entries"}</span>
        </div>
        {direct.length > 0 ? (
          <TierTable rows={direct} liveAvailable={liveAvailable} />
        ) : (
          <div className="muted boardSheetHint">No jobs need direct attention — the plan's near-term changes are all downstream.</div>
        )}
      </div>

      <div className="boardSheetTier boardSheetTierDownstream">
        <div className="boardSheetTierHead">
          <span className="boardSheetGrade boardSheetGradeDownstream">③</span>{" "}
          <strong>Downstream</strong> — no Plex entry needed{" "}
          <span className="muted boardSheetHint">once {yours.length > 0 ? "① and ②" : "②"} {yours.length > 0 ? "are" : "is"} in, Plex replans these itself; the next import confirms</span>{" "}
          {downstream.length > 0 ? (
            <button type="button" className="btn btnSm" onClick={() => setShowDownstream((v) => !v)}>
              {showDownstream ? "Hide" : `Show ${downstream.length} ${downstream.length === 1 ? "job" : "jobs"}`}
            </button>
          ) : (
            <span className="muted boardSheetHint">· none</span>
          )}
        </div>
        {showDownstream && downstream.length > 0 ? <TierTable rows={downstream} liveAvailable={liveAvailable} /> : null}
      </div>

      <div className="help boardSheetFoot">
        The path: enter {yours.length > 0 ? "① (and ② where needed)" : "②"} in Plex → Plex replans the rest on its own → the next schedule import shows the Plex schedule
        lining up with this plan, and ③ resolves itself. Atlas never writes to Plex — these are instructions for a person.
      </div>
    </div>
  );
}
